// Slide-over drawer base. Dims background, closes on Esc or backdrop click.
import { useEffect, type ReactNode } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  width?: 'sm' | 'md' | 'lg';
  children: ReactNode;
}

export default function Drawer({ open, onClose, title, width = 'md', children }: Props) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  const widthClass = width === 'sm' ? 'max-w-sm' : width === 'lg' ? 'max-w-2xl' : 'max-w-md';

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-ink-950/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`absolute top-0 right-0 h-full w-full ${widthClass} bg-ink-900 border-l border-ink-800 shadow-2xl flex flex-col`}>
        <div className="flex items-center justify-between p-4 border-b border-ink-800">
          <h2 className="font-semibold">{title}</h2>
          <button onClick={onClose} className="text-mono-white/60 hover:text-mono-white text-sm">Close · Esc</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}
